import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listAccounts } from "../../lib/api-modules/accounts.js";
import type { AccountSummary, HousingUnit } from "../../lib/api-modules/types.js";
import { refinanceBreakeven } from "../../lib/util/mortgage.js";
import { formatCentsCurrency as formatCents } from "../../lib/util/money.js";

/**
 * Refinance break-even tab. Pure client-side calculator over an
 * existing linked mortgage account. Computes monthly payment delta,
 * months-to-breakeven, and lifetime interest savings vs. closing
 * costs. Phase 7 (Tax) will layer in MID + AGI-aware deduction
 * scenarios; this tab is the cash-flow first cut.
 */

interface RefinanceTabProps {
  readonly unit: HousingUnit;
}

export function RefinanceTab({ unit }: RefinanceTabProps) {
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => listAccounts(),
  });

  const linkedMortgages = useMemo<AccountSummary[]>(() => {
    const ids = new Set(unit.linked_account_ids ?? []);
    return (accountsQuery.data?.accounts ?? []).filter(
      (a) =>
        ids.has(a.account_id) &&
        (a.account_type === "mortgage" || a.account_type === "second_mortgage"),
    );
  }, [accountsQuery.data, unit.linked_account_ids]);

  const [selectedId, setSelectedId] = useState<string>("");
  const [newRate, setNewRate] = useState<string>("");
  const [newTermYears, setNewTermYears] = useState<string>("30");
  const [closingCostsDollars, setClosingCostsDollars] = useState<string>("5000");

  const selected = linkedMortgages.find((a) => a.account_id === selectedId);

  const result = useMemo(() => {
    if (!selected) return null;
    const principal = selected.current_principal_cents;
    const currentRate = selected.interest_rate_pct;
    if (principal === undefined || currentRate === undefined) return null;
    const newRateNum = parseFloat(newRate);
    const newTerm = parseFloat(newTermYears);
    const closing = parseFloat(closingCostsDollars);
    if (
      !Number.isFinite(newRateNum) ||
      !Number.isFinite(newTerm) ||
      !Number.isFinite(closing) ||
      newTerm <= 0
    ) {
      return null;
    }

    // Months remaining on the current loan: derive from payoff_date or
    // default to 360.
    const today = new Date();
    let monthsRemaining = 360;
    if (selected.payoff_date) {
      const payoff = new Date(selected.payoff_date);
      monthsRemaining = Math.max(
        1,
        (payoff.getUTCFullYear() - today.getUTCFullYear()) * 12 +
          (payoff.getUTCMonth() - today.getUTCMonth()),
      );
    }

    return refinanceBreakeven({
      current_loan: {
        principal_cents: principal,
        rate_pct_yearly: currentRate,
        months_remaining: monthsRemaining,
        ...(selected.minimum_payment_cents !== undefined
          ? { monthly_payment_cents: selected.minimum_payment_cents }
          : {}),
      },
      new_loan: {
        principal_cents: principal,
        rate_pct_yearly: newRateNum,
        months_remaining: Math.round(newTerm * 12),
      },
      closing_costs_cents: Math.round(closing * 100),
    });
  }, [selected, newRate, newTermYears, closingCostsDollars]);

  if (accountsQuery.isLoading) {
    return <p className="text-sm text-zinc-500">Loading linked accounts…</p>;
  }
  if (linkedMortgages.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
        No mortgage accounts linked to this unit. Add one at{" "}
        <a href="/accounts" className="text-brand-600 hover:underline">
          /accounts
        </a>{" "}
        and link it via the unit edit form.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-zinc-500">
        Cash-flow first-cut analysis. Phase 7 layers on mortgage interest
        deduction (MID) impact, AGI-aware caps, and ARM crossover modeling.
      </p>

      <section className="grid grid-cols-1 gap-3 rounded-md border border-zinc-200 p-3 dark:border-zinc-800 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium">
            Loan to refinance
          </label>
          <select
            className={inputClass}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">— select —</option>
            {linkedMortgages.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.name} ({a.account_type})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            New rate (% annual)
          </label>
          <input
            type="number"
            step="0.001"
            className={inputClass}
            value={newRate}
            onChange={(e) => setNewRate(e.target.value)}
            placeholder="5.5"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            New term (years)
          </label>
          <input
            type="number"
            className={inputClass}
            value={newTermYears}
            onChange={(e) => setNewTermYears(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">
            Closing costs ($)
          </label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            value={closingCostsDollars}
            onChange={(e) => setClosingCostsDollars(e.target.value)}
          />
        </div>
      </section>

      {result && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label="Months to break even"
            value={
              result.months_to_breakeven === null
                ? "—"
                : `${result.months_to_breakeven} mo`
            }
            hint={
              result.months_to_breakeven === null
                ? "Monthly payment increases — closing costs never recover."
                : `≈ ${(result.months_to_breakeven / 12).toFixed(1)} years`
            }
          />
          <Tile
            label="Monthly savings"
            value={formatCents(result.monthly_payment_delta_cents)}
            hint={
              result.monthly_payment_delta_cents > 0
                ? "Cash-flow positive."
                : "Cash-flow negative — refi costs more per month."
            }
          />
          <Tile
            label="Lifetime interest savings"
            value={formatCents(result.lifetime_interest_savings_cents)}
            hint="Over the new loan's full term."
          />
          <Tile
            label="Current monthly payment"
            value={formatCents(result.current_monthly_payment_cents)}
            hint=""
          />
          <Tile
            label="New monthly payment"
            value={formatCents(result.new_monthly_payment_cents)}
            hint=""
          />
        </section>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-1 font-mono text-base">{value}</div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-zinc-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-60";
